import React from 'react';
import { Box, Container, Typography, AppBar, Toolbar, Stack, ThemeProvider, CssBaseline, useTheme } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import { inputsCustomizations } from '../../shared-theme/customizations/inputs';
import { dataDisplayCustomizations } from '../../shared-theme/customizations/dataDisplay';
import { feedbackCustomizations } from '../../shared-theme/customizations/feedback';
import { navigationCustomizations } from '../../shared-theme/customizations/navigation';
import { surfacesCustomizations } from '../../shared-theme/customizations/surfaces';
import { colorSchemes, typography, shadows, shape } from '../../shared-theme/themePrimitives';
import { PodcastIcon } from '@/components/icons/PodcastIcon';

function PrivacyContent() {
  const theme = useTheme();

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
       {/* Simple Navbar */}
       <AppBar 
        position="sticky" 
        elevation={0}
        sx={{ 
            borderBottom: '1px solid',
            borderColor: 'divider',
            backdropFilter: 'blur(12px)',
            backgroundColor: 'rgba(255, 255, 255, 0.8)', 
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ height: 64 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ cursor: 'pointer' }} onClick={() => window.location.href = '/'}>
               <PodcastIcon size={32} color={theme.palette.primary.main} />
               <Typography variant="h6" fontWeight={800} sx={{ letterSpacing: '-0.5px', color: 'text.primary' }}>
                Podstack
              </Typography>
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 8 }}>
        <Typography variant="h2" fontWeight={800} sx={{ mb: 2 }}>Privacy Policy</Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 6 }}>Last updated: {new Date().toLocaleDateString()}</Typography>
        
        <Stack spacing={4}>
            <Box>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>1. Information Collection</Typography>
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    We collect information to provide better services to all our users. This includes:
                </Typography>
                <ul style={{ color: '#666', marginTop: 10, lineHeight: 1.7, paddingLeft: 20 }}>
                    <li><strong>Personal Information:</strong> Name, email address, and billing information when you register for an account.</li>
                    <li><strong>Usage Data:</strong> Information about how you interact with our Service, including pages visited, time spent, and other diagnostic data.</li>
                    <li><strong>Cookies and Tracking Technologies:</strong> We use cookies to track the activity on our Service and hold certain information.</li>
                </ul>
            </Box>

             <Box>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>2. Use of Data</Typography>
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                     Podstack uses the collected data for various purposes:
                </Typography>
                <ul style={{ color: '#666', marginTop: 10, lineHeight: 1.7, paddingLeft: 20 }}>
                    <li>To provide and maintain the Service;</li>
                    <li>To notify you about changes to our Service;</li>
                    <li>To allow you to participate in interactive features of our Service when you choose to do so;</li>
                    <li>To provide customer care and support;</li>
                    <li>To provide analysis or valuable information so that we can improve the Service;</li>
                </ul>
            </Box>

             <Box>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>3. Data Retention</Typography>
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    Podstack Inc. will retain your Personal Data only for as long as is necessary for the purposes set out in this Privacy Policy. We will retain and use your Personal Data to the extent necessary to comply with our legal obligations (for example, if we are required to retain your data to comply with applicable laws), resolve disputes, and enforce our legal agreements and policies.
                </Typography>
            </Box>

            <Box>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>4. Service Providers</Typography>
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                     We may employ third party companies and individuals to facilitate our Service ("Service Providers"), to provide the Service on our behalf, to perform Service-related services or to assist us in analyzing how our Service is used. These third parties have access to your Personal Data only to perform these tasks on our behalf and are obligated not to disclose or use it for any other purpose.
                </Typography>
            </Box>

            <Box>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>5. Data Security</Typography>
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                     The security of your data is important to us, but remember that no method of transmission over the Internet, or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your Personal Data, we cannot guarantee its absolute security.
                </Typography>
            </Box>

            <Box>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>6. Children's Privacy</Typography>
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                     Our Service does not address anyone under the age of 18 ("Children"). We do not knowingly collect personally identifiable information from anyone under the age of 18. If you are a parent or guardian and you are aware that your Children has provided us with Personal Data, please contact us.
                </Typography>
            </Box>

             <Box>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>7. Contact Us</Typography>
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                     If you have any questions about this Privacy Policy, please contact us at support@podstack.co.
                </Typography>
            </Box>
        </Stack>
      </Container>


       {/* Simple Footer */}
       <Box sx={{ py: 6, bgcolor: 'white', borderTop: '1px solid', borderColor: 'divider', mt: 'auto' }}>
            <Container maxWidth="lg">
                <Typography variant="body2" color="text.disabled" align="center">
                    © {new Date().getFullYear()} Podstack Inc.
                </Typography>
            </Container>
        </Box>

    </Box>
  );
}


export default function PrivacyPage() {
    const lightTheme = React.useMemo(() => createTheme({
      cssVariables: {
        colorSchemeSelector: 'data-mui-color-scheme',
        cssVarPrefix: 'template',
      },
      colorSchemes, 
      typography,
      shadows,
      shape,
      components: {
        ...inputsCustomizations,
        ...dataDisplayCustomizations,
        ...feedbackCustomizations,
        ...navigationCustomizations,
        ...surfacesCustomizations,
      },
    }), []);
  
    return (
      <ThemeProvider theme={lightTheme}>
        <CssBaseline />
        <PrivacyContent />
      </ThemeProvider>
    );
  }
